/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// react-router-dom components
import { Link, useLocation } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

import { useState } from "react";
import MDSnackbar from "components/MDSnackbar";
import CircularProgress from "@mui/material/CircularProgress";
import useAuth from "auth/useAuth";
import CoverLayout from "pages/authentication/components/CoverLayout";
import typography from "assets/theme/base/typography";
import SSOButton from "components/atoms/Buttons/SSOButton";

const { size } = typography;

function Cover() {
  const [errorSB, setErrorSB] = useState(false);
  const { search } = useLocation();

  const showJumpcloud = search?.includes?.("sso=jumpcloud");

  const { signUp, isLoading, error } = useAuth();

  const closeErrorSB = () => setErrorSB(false);

  const [formValues, setFormValues] = useState({
    email: {
      value: "",
      error: false,
      errorMessage: "",
    },
  });

  const signUpHandler = async (event) => {
    event.preventDefault();

    const formFields = Object.keys(formValues);
    let newFormValues = { ...formValues };

    for (let index = 0; index < formFields.length; index += 1) {
      const currentField = formFields[index];
      const currentValue = formValues[currentField].value;

      if (currentValue === "") {
        newFormValues = {
          ...newFormValues,
          [currentField]: {
            ...newFormValues[currentField],
            error: true,
            errorMessage: "Field is required",
          },
        };
      }
    }

    setFormValues({ ...newFormValues });
    const foundErrorValue = Object.values(newFormValues).find((value) => value.error === true);

    if (foundErrorValue) {
      // show error message
      setErrorSB(true);
    } else {
      const dataToPass = {
        email: newFormValues.email.value,
        // timezone: momentTz.tz.guess(), // TODO: remove
      };

      signUp(dataToPass);

      setFormValues({
        email: {
          value: "",
          error: false,
          errorMessage: "",
        },
      });
    }
  };

  const onChangeHandler = async (e) => {
    const { name, value } = e.target;
    if (name === "email") {
      if (value !== "") {
        if (
          !value
            .toLowerCase()
            .match(
              /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            )
        ) {
          setFormValues({
            ...formValues,
            [name]: {
              ...formValues[name],
              value,
              error: true,
              errorMessage: "Email is not in proper format",
            },
          });
        } else if (value.length > 254) {
          setFormValues({
            ...formValues,
            [name]: {
              ...formValues[name],
              value,
              error: true,
              errorMessage: "Email too long",
            },
          });
        } else {
          setFormValues({
            ...formValues,
            [name]: {
              ...formValues[name],
              value,
              error: false,
              errorMessage: "",
            },
          });
        }
      } else {
        setFormValues({
          ...formValues,
          [name]: {
            ...formValues[name],
            value,
            error: true,
            errorMessage: "Email field is required",
          },
        });
      }
    } else {
      setFormValues({
        ...formValues,
        [name]: {
          ...formValues[name],
          value,
          error: false,
          errorMessage: "",
        },
      });
    }
  };

  return (
    <CoverLayout>
      {isLoading ? (
        <Stack alignItems="center">
          <CircularProgress
            sx={{
              position: "fixed",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 2,
            }}
            color="info"
            size="10vh"
          />
        </Stack>
      ) : (
        ""
      )}
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="secondary"
          mx={2}
          mt={-3}
          p={3}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            First enter your email
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox
            component="form"
            role="form"
            noValidate
            sx={{
              "& .MuiFormHelperText-root": { color: "#d32f2f" },
              "& .MuiInputLabel-formControl": { lineHeight: "20px !important" },
            }}
            onSubmit={signUpHandler}
          >
            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email*"
                variant="standard"
                fullWidth
                onChange={(e) => onChangeHandler(e)}
                value={formValues.email.value}
                name="email"
                error={formValues.email.error}
                helperText={formValues.email.errorMessage}
              />
            </MDBox>

            <MDSnackbar
              color="error"
              icon="warning"
              title="Required Field"
              content="All fields are required!"
              open={errorSB}
              onClose={closeErrorSB}
              close={closeErrorSB}
              bgWhite
            />

            <span style={{ color: "#d32f2f", fontSize: "12px" }}>{error}</span>
            <MDBox mt={4} mb={1}>
              <MDButton color="primaryRed" fullWidth onClick={signUpHandler}>
                sign up
              </MDButton>
            </MDBox>
            {showJumpcloud && (
              <MDBox mt={4} mb={1}>
                <SSOButton provider="jumpcloud" />
              </MDBox>
            )}
            <MDBox mt={4} mb={1}>
              <SSOButton provider="google" />
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography
                style={{
                  fontSize: size.xs,
                }}
              >
                By continuing, you agree with Alysio&apos;s{" "}
                <a href="https://www.alysio.ai/terms-of-service" target="_blank" rel="noreferrer">
                  Terms & Conditions
                </a>{" "}
                and{" "}
                <a href="https://www.alysio.ai/privacy-policy" target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>{" "}
              </MDTypography>

              <MDTypography variant="button" color="text">
                Already have an account?{" "}
                <MDTypography
                  component={Link}
                  to="/sign-in"
                  variant="button"
                  color="secondaryBlue"
                  fontWeight="medium"
                  fontSize=".8 rem"
                  pl="0.25rem"
                >
                  Log In
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </CoverLayout>
  );
}

export default Cover;
